import { SagaIterator } from 'redux-saga';
import { actionChannel, call, delay, flush, fork, put, race, take } from 'redux-saga/effects';
import { sendActivityStatusCommandNoResult } from './Socket/Commands';
import {
  ActivityWakeUp,
  callStatusHeartbeat,
  HydrateActivityStatuses,
  StatusHeartbeat,
  SubscribeContactListActivityStatuses,
} from '../Redux/ActivityStatusActions';
import { getLastUserActivityTime } from '../Services/GlobalObserver';
import { HEARTBEAT_INTERVAL } from '../Utils/time';
import { RootState } from '../Redux';
import { select as typedSelect } from 'typed-redux-saga';
import devLog from '../Utils/logger';

export function* batchRetrieveActivityStatusesSaga(): SagaIterator<void> {
  // Put all actions into a channel buffer
  const subChannel = yield actionChannel('ActivityStatus/ADD_ACTIVITY_STATUS_SUB');

  // Wait for the initial contact list loaded before subscribing to new ones
  // If server not response, continue with the subscriptions
  yield race({
    serverSocketResponse: take('ActivityStatus/RECEIVE_CONTACT_LIST_STATUSES'),
    timeout: delay(10000),
  });

  while (true) {
    const firstSubAction = yield take(subChannel);

    const statusIds = [firstSubAction.userId];
    yield delay(50);

    const pendingSubActions = yield flush(subChannel);

    for (const subAction of pendingSubActions) {
      if (!statusIds.includes(subAction.userId)) {
        statusIds.push(subAction.userId);
      }
    }

    const userId = yield* typedSelect((state: RootState) => state.auth.profile?.userId);

    if (!userId) {
      console.error('[SAGA ERROR: User is not authenticated for status socket');
      devLog('socket', ['User is not authenticated for status socket']);
      continue;
    }

    if (statusIds.length > 0) {
      yield call(sendActivityStatusCommandNoResult, userId, 'subscribe-batch-statuses', [userId, statusIds]);
    }
  }
}

export function* retrieveContactListActivityStatusesSaga(
  action: SubscribeContactListActivityStatuses,
): SagaIterator<void> {
  // Check the current socket status being connected, pause until connected back
  const socketStatus = yield* typedSelect((state: RootState) => state.activityStatuses.socketStatus);

  if (socketStatus !== 'connected') {
    yield take('ActivityStatus/SOCKET_STATUS');
  }

  yield call(sendActivityStatusCommandNoResult, action.userId, 'subscribe-contact-statuses', [action.userId]);
}

export function* hydrateRetrieveActivityStatusesSaga(action: HydrateActivityStatuses): SagaIterator<void> {
  // This Saga is different from the batchRetrieve because we already has a list of user ids we need to rehydrate
  // This Saga is primarily for socket disconnect-reconnect flow
  yield call(sendActivityStatusCommandNoResult, action.userId, 'subscribe-batch-statuses', [
    action.userId,
    action.statusIds,
  ]);
}

export function* getActivityStatusHeartbeatSaga(action: StatusHeartbeat): SagaIterator<void> {
  while (true) {
    const now = Date.now();

    // Get last user activity time from global observer
    const timeSinceLastActivity = now - getLastUserActivityTime();

    // NOTE: Only send the status heartbeat if the user's last activity is within the heartbeat 5 min interval
    //     : If the user is "away" after 5 min, then no need to send the check-in
    //          -> (though they technically still active because they have not been marked "inactive" in server for over 20 min)
    //     : If the user is active again (within 20 min), then we resume the check-in when the 5 min mark loop back
    //     : If the user is active again but outside of 20 min mark, then the wake up Saga will take care of it and restart the whole Saga
    if (timeSinceLastActivity < HEARTBEAT_INTERVAL) {
      yield call(sendActivityStatusCommandNoResult, action.userId, 'status-heartbeat', [action.userId]);
    }

    yield delay(HEARTBEAT_INTERVAL);
  }
}

export function* activityInstantWakeUpSaga(action: ActivityWakeUp): SagaIterator<void> {
  // Check the current socket status being connected, pause until connected back
  const socketStatus = yield* typedSelect((state: RootState) => state.activityStatuses.socketStatus);

  if (socketStatus !== 'connected') {
    yield take('ActivityStatus/SOCKET_STATUS');
  }

  // Signaling the server to update user status to active and check-in the heartbeat
  yield call(sendActivityStatusCommandNoResult, action.userId, 'client-activity', [action.userId]);

  // Lock the Saga until the server response with STATUS_UPDATE
  // or drop after 10 seconds when server is not responding
  yield race({
    serverSocketResponse: take('ActivityStatus/STATUS_UPDATE'),
    timeout: delay(10000),
  });

  // Start the status heartbeat back
  yield put(callStatusHeartbeat(action.userId));
}
